import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GPX_ROUTES } from '@rsos/gainsight';
import {
  isCallerIDFromOrg,
  EmergencyCallsMap,
} from '@rsos/utils/isCallerIDFromOrg';

const SXM_ORG = 'sxm';

type HoverRoute = typeof GPX_ROUTES.CALL_QUEUE | typeof GPX_ROUTES.MAP;

interface HoverStartParams {
  callerID: string | undefined;
  payloadID: string | null;
  route: HoverRoute;
  callStartTime: number | null | undefined;
  irpVersion?: string;
  isConnectedCar?: boolean;
}

interface CallHoverTracking {
  trackHoverStart: (params: HoverStartParams) => void;
  trackHoverEnd: () => void;
}

const CallHoverTrackingContext = createContext<CallHoverTracking | null>(null);

// The `call_hover` analytics event was removed to cut Amplitude ingestion
// volume — it generated ~10M events/month for low analytical value and made up
// a large share of the monthly quota. The hook API is intentionally kept as a
// no-op so the hover call sites (map layers, call queue) don't need to change,
// and hover tracking can be reintroduced later — ideally sampled — without
// re-wiring every consumer.
export function CallHoverTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const value = useMemo<CallHoverTracking>(
    () => ({
      trackHoverStart: () => {
        // no-op: call_hover tracking removed to reduce ingestion volume
      },
      trackHoverEnd: () => {
        // no-op: call_hover tracking removed to reduce ingestion volume
      },
    }),
    [],
  );

  return (
    <CallHoverTrackingContext.Provider value={value}>
      {children}
    </CallHoverTrackingContext.Provider>
  );
}

export function useCallHoverTracking(): CallHoverTracking {
  const context = useContext(CallHoverTrackingContext);
  if (!context) {
    throw new Error(
      'useCallHoverTracking must be used within a CallHoverTrackingProvider',
    );
  }
  return context;
}

type ConnectedCarLookup = (callerID: string | undefined) => boolean;

export function useConnectedCarLookup(): ConnectedCarLookup {
  const callQueue = useSelector(
    (state: { emergencyCalls: { callQueue: EmergencyCallsMap } }) =>
      state.emergencyCalls.callQueue,
  );
  return useCallback(
    (callerID: string | undefined): boolean =>
      isCallerIDFromOrg(SXM_ORG, callQueue, callerID),
    [callQueue],
  );
}
